import { useCallback, useEffect, useState, useRef } from 'react'
import { createPortal } from 'react-dom'
import { useAnnotationsContext } from './Annotations/AnnotationsContext'

type ResizeDirection =
  | 'top'
  | 'right'
  | 'bottom'
  | 'left'
  | 'topLeft'
  | 'topRight'
  | 'bottomLeft'
  | 'bottomRight'

type ElementResizeOverlayProps = {
  isVisible: boolean
  containerRef: React.RefObject<HTMLDivElement | null>
  maxZIndex: number
  enabledHandlers?: Partial<Record<ResizeDirection, boolean>>
  currentBoundingWidth: number // Visible width after crop
  currentBoundingHeight: number // Visible height after crop
  zoom: number
  lockAspectRatio?: boolean
  minWidth?: number
  minHeight?: number
  showOutline?: boolean // Show border outline around the element
  outlineClassName?: string // CSS classes for the outline (e.g., 'border border-indigo-400', 'border-4 rounded-md')
  outlineBorderWidth?: number // Border width in pixels - handlers will be positioned inside this border
  onResizeStart: () => void
  onResize: (
    boundingWidth: number,
    boundingHeight: number,
    direction: ResizeDirection,
  ) => void
  onResizeStop: (
    boundingWidth: number,
    boundingHeight: number,
    direction: ResizeDirection,
  ) => void
}

const HANDLE_SIZE = 8 // Should match the CSS size of the handle

const getLocalRect = ({
  portalElement,
  rect,
}: {
  portalElement: HTMLElement
  rect: DOMRect
}) => {
  const portalRect = portalElement.getBoundingClientRect()
  const scaleX =
    portalRect.width > 0 && portalElement.offsetWidth > 0
      ? portalRect.width / portalElement.offsetWidth
      : 1
  const scaleY =
    portalRect.height > 0 && portalElement.offsetHeight > 0
      ? portalRect.height / portalElement.offsetHeight
      : 1

  return {
    bottom: (rect.bottom - portalRect.top) / scaleY,
    height: rect.height / scaleY,
    left: (rect.left - portalRect.left) / scaleX,
    right: (rect.right - portalRect.left) / scaleX,
    top: (rect.top - portalRect.top) / scaleY,
    width: rect.width / scaleX,
  }
}

export const ElementResizeOverlay = ({
  isVisible,
  containerRef,
  maxZIndex,
  enabledHandlers = {
    topLeft: true,
    topRight: true,
    bottomLeft: true,
    bottomRight: true,
  },
  currentBoundingWidth,
  currentBoundingHeight,
  zoom,
  lockAspectRatio = true,
  minWidth = 50,
  minHeight = 50,
  showOutline = false,
  outlineClassName = 'border border-indigo-400',
  outlineBorderWidth = 0,
  onResizeStart,
  onResize,
  onResizeStop,
}: ElementResizeOverlayProps) => {
  const { floatingOverlayElement } = useAnnotationsContext()
  const zoomRatio = zoom / 100
  const [isResizing, setIsResizing] = useState(false)
  const resizeInfoRef = useRef<{
    direction: ResizeDirection | null
    startX: number
    startY: number
    startBoundingWidth: number
    startBoundingHeight: number
  }>({
    direction: null,
    startX: 0,
    startY: 0,
    startBoundingWidth: 0,
    startBoundingHeight: 0,
  })
  const [handlerPositions, setHandlerPositions] = useState<
    Record<ResizeDirection, { top: number; left: number }>
  >({
    top: { top: 0, left: 0 },
    right: { top: 0, left: 0 },
    bottom: { top: 0, left: 0 },
    left: { top: 0, left: 0 },
    topLeft: { top: 0, left: 0 },
    topRight: { top: 0, left: 0 },
    bottomLeft: { top: 0, left: 0 },
    bottomRight: { top: 0, left: 0 },
  })
  const [outlineRect, setOutlineRect] = useState<{
    top: number
    left: number
    width: number
    height: number
  }>({ top: 0, left: 0, width: 0, height: 0 })

  const handleMouseDown = useCallback(
    (direction: ResizeDirection) => (e: React.MouseEvent<HTMLDivElement>) => {
      e.preventDefault()
      e.stopPropagation()

      setIsResizing(true)
      resizeInfoRef.current = {
        direction,
        startX: e.clientX,
        startY: e.clientY,
        startBoundingWidth: currentBoundingWidth,
        startBoundingHeight: currentBoundingHeight,
      }
      onResizeStart()
    },
    [currentBoundingWidth, currentBoundingHeight, onResizeStart],
  )

  useEffect(() => {
    if (!isResizing) return

    const handleMouseMove = (e: MouseEvent) => {
      if (!resizeInfoRef.current.direction) return

      const deltaX = (e.clientX - resizeInfoRef.current.startX) / zoomRatio
      const deltaY = (e.clientY - resizeInfoRef.current.startY) / zoomRatio

      const direction = resizeInfoRef.current.direction
      let newBoundingWidth = resizeInfoRef.current.startBoundingWidth
      let newBoundingHeight = resizeInfoRef.current.startBoundingHeight

      // Calculate new dimensions based on direction
      if (
        direction === 'right' ||
        direction === 'topRight' ||
        direction === 'bottomRight'
      ) {
        newBoundingWidth = resizeInfoRef.current.startBoundingWidth + deltaX
      } else if (
        direction === 'left' ||
        direction === 'topLeft' ||
        direction === 'bottomLeft'
      ) {
        newBoundingWidth = resizeInfoRef.current.startBoundingWidth - deltaX
      }

      if (
        direction === 'bottom' ||
        direction === 'bottomLeft' ||
        direction === 'bottomRight'
      ) {
        newBoundingHeight = resizeInfoRef.current.startBoundingHeight + deltaY
      } else if (
        direction === 'top' ||
        direction === 'topLeft' ||
        direction === 'topRight'
      ) {
        newBoundingHeight = resizeInfoRef.current.startBoundingHeight - deltaY
      }

      // Apply aspect ratio constraint
      if (lockAspectRatio) {
        const aspectRatio =
          resizeInfoRef.current.startBoundingWidth /
          resizeInfoRef.current.startBoundingHeight
        if (Math.abs(deltaX) > Math.abs(deltaY)) {
          newBoundingHeight = newBoundingWidth / aspectRatio
        } else {
          newBoundingWidth = newBoundingHeight * aspectRatio
        }
      }

      // Apply min constraints
      newBoundingWidth = Math.max(newBoundingWidth, minWidth)
      newBoundingHeight = Math.max(newBoundingHeight, minHeight)

      onResize(newBoundingWidth, newBoundingHeight, direction)
    }

    const handleMouseUp = () => {
      if (!resizeInfoRef.current.direction) return

      const direction = resizeInfoRef.current.direction
      const finalBoundingWidth = currentBoundingWidth
      const finalBoundingHeight = currentBoundingHeight

      setIsResizing(false)
      resizeInfoRef.current = {
        direction: null,
        startX: 0,
        startY: 0,
        startBoundingWidth: 0,
        startBoundingHeight: 0,
      }

      onResizeStop(finalBoundingWidth, finalBoundingHeight, direction)
    }

    document.addEventListener('mousemove', handleMouseMove)
    document.addEventListener('mouseup', handleMouseUp)

    return () => {
      document.removeEventListener('mousemove', handleMouseMove)
      document.removeEventListener('mouseup', handleMouseUp)
    }
  }, [
    isResizing,
    currentBoundingWidth,
    currentBoundingHeight,
    zoomRatio,
    lockAspectRatio,
    minWidth,
    minHeight,
    onResize,
    onResizeStop,
  ])

  const updateHandlerPositions = useCallback(() => {
    if (!containerRef.current) return
    const rect = containerRef.current.getBoundingClientRect()
    const positioningRect = floatingOverlayElement
      ? getLocalRect({
          portalElement: floatingOverlayElement,
          rect,
        })
      : rect

    const centerX = positioningRect.left + positioningRect.width / 2
    const centerY = positioningRect.top + positioningRect.height / 2

    // Position handlers at the element edges (inside the outline border)
    setHandlerPositions({
      top: {
        top: positioningRect.top + HANDLE_SIZE / 2,
        left: centerX,
      },
      right: {
        top: centerY,
        left: positioningRect.right - HANDLE_SIZE / 2,
      },
      bottom: {
        top: positioningRect.bottom - HANDLE_SIZE / 2,
        left: centerX,
      },
      left: {
        top: centerY,
        left: positioningRect.left + HANDLE_SIZE / 2,
      },
      topLeft: {
        top: positioningRect.top + HANDLE_SIZE / 2,
        left: positioningRect.left + HANDLE_SIZE / 2,
      },
      topRight: {
        top: positioningRect.top + HANDLE_SIZE / 2,
        left: positioningRect.right - HANDLE_SIZE / 2,
      },
      bottomLeft: {
        top: positioningRect.bottom - HANDLE_SIZE / 2,
        left: positioningRect.left + HANDLE_SIZE / 2,
      },
      bottomRight: {
        top: positioningRect.bottom - HANDLE_SIZE / 2,
        left: positioningRect.right - HANDLE_SIZE / 2,
      },
    })

    // With content-box, position outline outward by border width
    setOutlineRect({
      top: positioningRect.top - outlineBorderWidth,
      left: positioningRect.left - outlineBorderWidth,
      width: positioningRect.width,
      height: positioningRect.height,
    })
  }, [containerRef, floatingOverlayElement, outlineBorderWidth])

  useEffect(() => {
    if (!isVisible) return

    updateHandlerPositions()

    let raf = 0
    const tick = () => {
      updateHandlerPositions()
      raf = requestAnimationFrame(tick)
    }
    raf = requestAnimationFrame(tick)

    const onScroll = () => updateHandlerPositions()
    const onResize = () => updateHandlerPositions()

    window.addEventListener('scroll', onScroll, { passive: true })
    window.addEventListener('resize', onResize)

    return () => {
      cancelAnimationFrame(raf)
      window.removeEventListener('scroll', onScroll)
      window.removeEventListener('resize', onResize)
    }
  }, [isVisible, updateHandlerPositions])

  if (!isVisible) return null

  const handlers: Array<{
    direction: ResizeDirection
    position: { top: number; left: number }
    cursor: string
  }> = [
    { direction: 'top', position: handlerPositions.top, cursor: 'ns-resize' },
    {
      direction: 'right',
      position: handlerPositions.right,
      cursor: 'ew-resize',
    },
    {
      direction: 'bottom',
      position: handlerPositions.bottom,
      cursor: 'ns-resize',
    },
    { direction: 'left', position: handlerPositions.left, cursor: 'ew-resize' },
    {
      direction: 'topLeft',
      position: handlerPositions.topLeft,
      cursor: 'nwse-resize',
    },
    {
      direction: 'topRight',
      position: handlerPositions.topRight,
      cursor: 'nesw-resize',
    },
    {
      direction: 'bottomLeft',
      position: handlerPositions.bottomLeft,
      cursor: 'nesw-resize',
    },
    {
      direction: 'bottomRight',
      position: handlerPositions.bottomRight,
      cursor: 'nwse-resize',
    },
  ]

  const positionStyle = floatingOverlayElement ? 'absolute' : 'fixed'
  const portalElement = floatingOverlayElement ?? document.body

  return createPortal(
    <>
      {/* Outline border */}
      {showOutline && outlineRect.width > 0 && (
        <div
          className={outlineClassName}
          style={{
            position: positionStyle,
            top: `${outlineRect.top}px`,
            left: `${outlineRect.left}px`,
            width: `${outlineRect.width - 1}px`,
            height: `${outlineRect.height - 1}px`,
            pointerEvents: 'none',
            zIndex: maxZIndex + 2,
            boxSizing: 'content-box',
            willChange: 'top, left, width, height',
          }}
        />
      )}

      {/* Resize handlers */}
      {handlers
        .filter(({ direction }) => enabledHandlers[direction])
        .map(({ direction, position, cursor }) => (
          <div
            key={direction}
            className="resize-handle"
            style={{
              position: positionStyle,
              top: `${position.top}px`,
              left: `${position.left}px`,
              transform: 'translate(-50%, -50%)',
              cursor,
              zIndex: maxZIndex + 2,
              pointerEvents: 'auto',
              margin: 0, // Override the CSS margin to fix positioning
              willChange: 'top, left',
            }}
            onMouseDown={handleMouseDown(direction)}
          />
        ))}
    </>,
    portalElement,
  )
}
